import React, { useState, useContext, createContext, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';

import { Drawer } from 'antd';
import { UpdateCalendarContext } from 'Contexts';

import { useCloseAllDrawers } from '@contexts/LayoutContext';
import { useLayoutContext } from '@contexts/LayoutContext';
import { useStateValue } from '@contexts/StateProvider';

import {
    fetchCustomersByArea,
    fetchForAccountingArea,
    fetchForManagementArea,
    fetchModelsByArea,
} from '@hooks/API/Contact/UseContactsList';

import HeaderGlobalAddButton from '@components/_LayoutComponents/Header/HeaderGlobalAddButton';
import HeaderPackagesButton from '@components/_LayoutComponents/Header/HeaderPackagesButton';
import NwNavBarWrapper from '@components/_LayoutComponents/Header/NavBar/NwNavBarWrapper';
import { getAreaUrl } from '@components/_LayoutComponents/Header/NavBar/NwNavUtils';
import AreaSelect from '@components/_LayoutComponents/Layout/AreaSelect';

import EventDrawer from '@components/EventDrawer/EventDrawer';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import GlobalSearchByArea from './GlobalSearch/GlobalSearchByArea';
import { StyledHeader } from './NwHeaderUI';
import UserDropDown from './UserDropDown';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';
import logo from '@img/nw.png';

export const HeaderContext = createContext(null);

const NwHeader = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const closeAllDrawers = useCloseAllDrawers();

    const [activeSearch, setActiveSearch] = useState(false);
    const [{ areaContext, loadingStatusContext }, dispatch] = useStateValue();

    const layoutContext = useLayoutContext();
    const { breakpoints, isMobile } = layoutContext;
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    const updateCalendarContext = useContext(UpdateCalendarContext);
    const [createdEvent, setCreatedEvent] = useState(null);

    const area = useMemo(() => {
        return areaContext.area;
    }, [areaContext.area]);

    const openMobileNav = () => {
        setMobileNavOpen(true);
    };

    const closeMobileNav = () => {
        setMobileNavOpen(false);
    };

    // const onSearchContact = useCallback((contactInfo) => {
    //     if (contactInfo.type === 'model') {
    //         if (area === 'accounting') {
    //             navigate(`/accounting/model/${contactInfo.ID}`);
    //         } else {
    //             navigate(`/model/${contactInfo.ID}/all/all`); //?clear_qf=true
    //         }
    //     } else {
    //         navigate(`/customer/${contactInfo.ID}/all/all`); //?clear_qf=true
    //     }
    // }, []);

    const onCloseEventDrawer = useCallback(() => {
        updateCalendarContext.setUpdateCalendar(true);
        setCreatedEvent(null);
    }, [updateCalendarContext]);

    const onViewSearch = useCallback((search) => {
        setActiveSearch(search);
    }, []);

    const onEventCreated = useCallback((event) => {
        setCreatedEvent(event);
    }, []);

    const onChangeArea = useCallback(
        async (areaToSet) => {
            dispatch({
                type: 'updateArea',
                area: areaToSet,
            });
            if (areaToSet === 'booking' || areaToSet === 'art') {
                await fetchModelsByArea();
                await fetchCustomersByArea();
            }
            if (areaToSet === 'management') {
                await fetchModelsByArea();
                await fetchCustomersByArea();
                await fetchForManagementArea();
            }
            // if (areaToSet === 'accounting') {
            //     await fetchForAccountingArea();
            // }
            const path = getAreaUrl(areaToSet, isMobile);

            await navigate(path);
            dispatch({
                type: 'updateContactsLoading',
                areaContactsLoading: false,
            });
            // } catch (error) {
            //     console.error('Fetch Area Customers and Models Error => ', error);
            // }
        },
        [dispatch, location.pathname]
    );

    const closeNavDrawer = useCallback(() => {
        closeMobileNav();
    }, []);

    const headerContextValues = useMemo(
        () => ({
            activeSearch,
            area,
            closeNavDrawer,
            onCloseEventDrawer,
            onEventCreated,
            onChangeArea,
            //onSearchContact,
            onViewSearch,
        }),
        [
            activeSearch,
            area,
            closeNavDrawer,
            onCloseEventDrawer,
            onEventCreated,
            onChangeArea,
            //onSearchContact,
            onViewSearch,
        ]
    );

    return (
        <HeaderContext.Provider value={headerContextValues}>
            {isMobile ? (
                <StyledHeader>
                    <div className="nw-header">
                        <div className="nw-header-sections-row">
                            <div className="nw-header-section nw-header-logo-container">
                                <div className="nw-header-logo-holder">
                                    <img src={logo} alt="NETWALK4" />
                                </div>
                            </div>
                            <div className="nw-header-section nw-header-main-section">
                                {area ? (
                                    <>
                                        <AreaSelect />
                                        {breakpoints < 3 ? (
                                            <div className="nw-main-nav-icon-wrapper">
                                                {mobileNavOpen ? (
                                                    <NwIcon
                                                        icon={byPrefixAndName.fasl['times']}
                                                        pointer
                                                        onClick={closeMobileNav}
                                                    />
                                                ) : (
                                                    <NwIcon
                                                        icon={byPrefixAndName.fasl['bars']}
                                                        pointer
                                                        onClick={openMobileNav}
                                                    />
                                                )}
                                            </div>
                                        ) : (
                                            <div className="nw-main-nav-wrapper">
                                                <NwNavBarWrapper />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <BeatLoader color={StyleVariables.PrimaryColor} size={8} />
                                )}
                            </div>
                        </div>
                        <div className="nw-header-sections-row nw-header-mobile-icons">
                            {/* <GlobalSearchByArea /> */}
                            {area !== 'management' && (
                                <>
                                    <div className="home-icon-holder">
                                        <NwIcon
                                            icon={byPrefixAndName.fasl['house-blank']}
                                            pointer
                                            onClick={() => {
                                                closeAllDrawers();
                                                navigate(getAreaUrl(area, isMobile));
                                            }}
                                        />
                                    </div>
                                    <HeaderGlobalAddButton />
                                    <HeaderPackagesButton />
                                </>
                            )}
                            <UserDropDown />
                        </div>
                    </div>
                </StyledHeader>
            ) : (
                // tablet and above (> 720px)
                <StyledHeader>
                    <div className="nw-header">
                        <div className="nw-header-section nw-header-logo-container">
                            {!activeSearch ? (
                                <div className="nw-header-logo-holder">
                                    <img src={logo} alt="NETWALK4" />
                                </div>
                            ) : (
                                <div />
                            )}
                            <GlobalSearchByArea />
                        </div>
                        <div className="nw-header-section nw-header-main-section">
                            {area ? (
                                <>
                                    <AreaSelect />
                                    {breakpoints < 3 ? (
                                        <div className="nw-main-nav-icon-wrapper">
                                            {mobileNavOpen ? (
                                                <NwIcon
                                                    icon={byPrefixAndName.fasl['times']}
                                                    pointer
                                                    onClick={closeMobileNav}
                                                />
                                            ) : (
                                                <NwIcon
                                                    icon={byPrefixAndName.fasl['bars']}
                                                    pointer
                                                    onClick={openMobileNav}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div className="nw-main-nav-wrapper">
                                            <NwNavBarWrapper />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <BeatLoader color={StyleVariables.PrimaryColor} size={8} />
                            )}
                        </div>
                        {area !== 'management' && (
                            <div className="nw-header-section nw-right-buttons">
                                <HeaderGlobalAddButton />
                                <HeaderPackagesButton />
                            </div>
                        )}
                        <div className="nw-header-section">
                            <UserDropDown />
                        </div>
                    </div>
                </StyledHeader>
            )}
            {mobileNavOpen && (
                <Drawer
                    className="mobile-nav-drawer"
                    placement="top"
                    onClose={closeMobileNav}
                    height={breakpoints > 1 ? '6.75rem' : '100vh'}
                    open
                    zIndex={isMobile ? StyleVariables.ZIndexNavMobile : StyleVariables.ZIndexNav}
                >
                    <NwNavBarWrapper drawer />
                </Drawer>
            )}
            {createdEvent && createdEvent.ShowDrawer && (
                <EventDrawer
                    drawerOrigin="header"
                    eventType={createdEvent.TypeName}
                    eventID={createdEvent.ID}
                    onClose={onCloseEventDrawer}
                    afterUpdate={() => {}}
                    afterRemove={() => {}}
                />
            )}
        </HeaderContext.Provider>
    );
};

export default NwHeader;
